'use client';

import type { Lead, LeadStatus } from '@up2stay/up2promo-api-client';
import { LeadContactChannel } from '@up2stay/up2promo-api-client';
import { formatLeadDisplayName } from '@up2stay/up2promo-commons';
import {
  Button,
  DetailListCard,
  DetailListRow,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Alert,
  AlertDescription,
  cn,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@up2stay/up2promo-ui';
import {
  Archive,
  ArrowUp,
  Calendar,
  Check,
  CircleDot,
  ContactRound,
  Ellipsis,
  Lock,
  Mail,
  MessageSquare,
  Paperclip,
  Phone,
  ScanLine,
  Search,
  Tag,
  Trash2,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';

import { LeadAssigneePicker } from '@/core/components/leads/lead-assignee-picker';
import { leadAssigneePickerLabelsFromDict } from '@/core/components/leads/lead-assignee-picker-labels';
import { LeadDetailAttachments } from '@/core/components/leads/lead-detail/lead-detail-attachments';
import { LeadDetailDescription } from '@/core/components/leads/lead-detail/lead-detail-description';
import { LEAD_NAME_MAX_LENGTH } from '@/core/components/leads/lead-detail/lead-detail-field-styles';
import {
  type ContactForm,
  type ContactFormField,
  type LeadCrmProps,
} from '@/core/components/leads/lead-detail/lead-detail-properties';
import type { LeadSaveToDeviceLabels } from '@/core/components/leads/lead-detail/lead-save-to-device-menu';
import { LeadDetailTimeline } from '@/core/components/leads/lead-detail/lead-detail-timeline';
import type { LeadNoteActionLabels } from '@/core/components/leads/lead-detail/lead-note-actions';
import {
  LEAD_STATUS_IDS,
  leadStatusAccent,
  leadStatusBadgeClass,
} from '@/core/components/leads/lead-status-colors';
import {
  formatLeadCapturedAt,
  leadInitials,
  personInitials,
} from '@/core/components/leads/leads-list-display';
import { PAGE_HEADER_CHROME_BUTTON_CLASS } from '@/core/components/page-header';
import {
  triggerErrorHaptic,
  triggerSelectionHaptic,
  triggerTapHaptic,
} from '@/core/lib/dnd-haptics';
import { isValidLeadEmail } from '@/core/lib/lead-email';
import { leadHasVcfContent } from '@/core/lib/leads-vcf';
import { isLeadArchived } from '@/core/lib/lead-mutate-access';
import { saveLeadToDevice, shareLeadVcf } from '@/core/lib/save-lead-to-device';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';
import { shellUsesMobileTabBar } from '@/core/utils/shell-chrome';
import {
  FilesApi,
  LeadAttachmentKind,
  LeadsApi,
} from '@up2stay/up2promo-api-client';

type FieldSheet =
  | 'status'
  | 'assignee'
  | 'visibility'
  | 'event'
  | 'tags'
  | 'email'
  | 'phone'
  | 'phone-actions'
  | 'herkunft'
  | 'contact-edit'
  | null;

type MobileFieldLabels = {
  noteActions: LeadNoteActionLabels;
  done: string;
  cancel: string;
  secureSave: string;
  assigneeShort: string;
  phone: string;
  phoneFieldHint: string;
  sendSms: string;
  copyAction: string;
  addAddress: string;
  createTag: string;
  readOnlyEditHint: string;
  editContactTitle: string;
  searchTags: string;
  status: string;
  email: string;
  visibility: string;
  visibilityPrivate: string;
  visibilityWorkspace: string;
  event: string;
  tags: string;
  origin: string;
  source: string;
  capturedBy: string;
  consent: string;
  consentYes: string;
  consentNo: string;
  call: string;
  sms: string;
  save: string;
  delete: string;
  edit: string;
  moreActions: string;
  editContact: string;
  archive: string;
  unarchive: string;
  archivedBanner: string;
};

type LeadDetailMobileProps = {
  lead: Lead;
  locale: string;
  detailDict: MobileFieldLabels & Record<string, unknown>;
  statusLabels: Record<string, string>;
  propertyLabels: {
    properties: string;
    status: string;
    email: string;
    phone: string;
    company: string;
    jobTitle: string;
    address: string;
    /** "{field} kopieren" — name of the copy buttons (DEV-540). */
    copyField: string;
  };
  createDict: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    workspace: string;
    jobTitle: string;
    address: string;
  };
  subtitle: string | null;
  sourceLabel: string;
  primaryEventName: string | null;
  contactForm: ContactForm;
  setContactForm: (
    form: ContactForm | ((prev: ContactForm) => ContactForm),
  ) => void;
  crm: LeadCrmProps;
  canManageCrm: boolean;
  canEditTags: boolean;
  canEditEvents: boolean;
  eventsEnabled: boolean;
  tagSuggestions: string[];
  workspaceEvents: Array<{ id: string; name: string }>;
  eventIds: string[];
  firstNameDraft: string;
  lastNameDraft: string;
  setFirstNameDraft: (v: string) => void;
  setLastNameDraft: (v: string) => void;
  timelineLabels: Parameters<typeof LeadDetailTimeline>[0]['labels'];
  attachmentLabels: Parameters<typeof LeadDetailAttachments>[0]['labels'];
  saveToDeviceLabels: LeadSaveToDeviceLabels;
  tabsLabels: { timeline: string; attachments: string };
  descriptionPlaceholder: string;
  messageFromLead?: string | null;
  messageLabel: string;
  isSavingDescription: boolean;
  isSubmittingComment: boolean;
  resolveAuthorDisplayName: (userId: string | undefined | null) => string;
  resolveAuthorInitials: (userId: string | undefined | null) => string;
  onStatusChange: (status: LeadStatus) => Promise<void> | void;
  /** Resolves false when the value was rejected (e.g. invalid email). */
  onContactFieldBlur: (
    field: ContactFormField,
    valueOverride?: string,
  ) => Promise<boolean | void> | void;
  /** Field message for an invalid email (DEV-571). */
  emailInvalidMessage: string;
  onContactVia: (channel: LeadContactChannel) => void;
  onCopy: () => void;
  onTagAdd: (tag: string) => void;
  onTagRemove: (tag: string) => void;
  onEventsAdd: (eventId: string) => void;
  onEventsRemove: (eventId: string) => void;
  onDescriptionSave: (value: string) => void;
  onSubmitComment: (content: string) => void;
  currentUserId?: string | null;
  onUpdateNote?: (noteId: string, content: string) => Promise<boolean>;
  onDeleteNote?: (noteId: string) => Promise<void>;
  onLeadUpdatedInCache: (lead: Lead) => void;
  onDeleteRequest: () => void;
  onSetArchived: (archived: boolean) => void;
  onFirstNameBlur: () => void;
  onLastNameBlur: () => void;
  /**
   * When true, ellipsis lives in PageHeader — do not render it in the hero.
   */
  overflowInHeader?: boolean;
  /** Increment to open the full contact-edit sheet (from header overflow). */
  contactEditRequestId?: number;
};

/** Overflow control for mobile lead detail — matches shared PageHeader chrome. */
export function LeadDetailMobileOverflowMenu({
  editContactLabel,
  archiveLabel,
  deleteLabel,
  menuAriaLabel,
  onEditContact,
  onArchive,
  onDelete,
  archiveDisabled = false,
}: {
  editContactLabel: string;
  archiveLabel: string;
  deleteLabel: string;
  menuAriaLabel: string;
  onEditContact: () => void;
  onArchive: () => void;
  onDelete: () => void;
  archiveDisabled?: boolean;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          mode="icon"
          className={PAGE_HEADER_CHROME_BUTTON_CLASS}
          aria-label={menuAriaLabel}
        >
          <Ellipsis className="size-[18px]" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onEditContact}>
          {editContactLabel}
        </DropdownMenuItem>
        <DropdownMenuItem disabled={archiveDisabled} onClick={onArchive}>
          {archiveLabel}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="size-4" aria-hidden />
          {deleteLabel}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SheetChrome({
  title,
  cancelLabel,
  doneLabel,
  onCancel,
  onDone,
  showDone = true,
  children,
}: {
  title: string;
  cancelLabel?: string;
  doneLabel?: string;
  onCancel: () => void;
  onDone?: () => void;
  showDone?: boolean;
  children: ReactNode;
}) {
  return (
    <>
      <DrawerHeader className="flex flex-row items-center justify-between gap-3 px-4 pb-3 pt-1 text-left sm:text-left">
        <button
          type="button"
          className="min-w-[4.5rem] text-left text-[15px] text-muted-foreground"
          onClick={onCancel}
        >
          {cancelLabel ?? ''}
        </button>
        <DrawerTitle className="text-center text-[16px] font-bold">
          {title}
        </DrawerTitle>
        {showDone ? (
          <button
            type="button"
            className="min-w-[4.5rem] text-right text-[15px] font-bold text-[color:var(--ghost-accent-fg)]"
            onClick={onDone}
          >
            {doneLabel}
          </button>
        ) : (
          <span className="min-w-[4.5rem]" aria-hidden />
        )}
      </DrawerHeader>
      <DrawerDescription className="sr-only">{title}</DrawerDescription>
      {children}
    </>
  );
}

export function LeadDetailMobile(props: LeadDetailMobileProps) {
  const {
    lead,
    locale,
    detailDict,
    statusLabels,
    propertyLabels,
    createDict,
    subtitle,
    sourceLabel,
    primaryEventName,
    contactForm,
    setContactForm,
    crm,
    canManageCrm,
    canEditTags,
    canEditEvents,
    eventsEnabled,
    tagSuggestions,
    workspaceEvents,
    eventIds,
    timelineLabels,
    attachmentLabels,
    saveToDeviceLabels,
    tabsLabels,
    descriptionPlaceholder,
    messageFromLead,
    messageLabel,
    isSavingDescription,
    isSubmittingComment,
    resolveAuthorDisplayName,
    resolveAuthorInitials,
    onStatusChange,
    onContactFieldBlur,
    emailInvalidMessage,
    onContactVia,
    onCopy,
    onTagAdd,
    onTagRemove,
    onEventsAdd,
    onEventsRemove,
    onDescriptionSave,
    onSubmitComment,
    currentUserId,
    onUpdateNote,
    onDeleteNote,
    onLeadUpdatedInCache,
    onDeleteRequest,
    onSetArchived,
    onFirstNameBlur,
    onLastNameBlur,
    firstNameDraft,
    lastNameDraft,
    setFirstNameDraft,
    setLastNameDraft,
    overflowInHeader = false,
    contactEditRequestId = 0,
  } = props;

  const [sheet, setSheet] = useState<FieldSheet>(null);
  const [textDraft, setTextDraft] = useState('');
  // Invalid email keeps the sheet open with the typed value (DEV-571).
  const [textError, setTextError] = useState<string | null>(null);
  const [contactEditEmailError, setContactEditEmailError] = useState<
    string | null
  >(null);
  const [tagDraft, setTagDraft] = useState('');
  const [pendingTags, setPendingTags] = useState<string[]>([]);
  const [pendingEventIds, setPendingEventIds] = useState<string[]>([]);
  const [noteDraft, setNoteDraft] = useState('');
  const [activeTab, setActiveTab] = useState('timeline');
  const [isUploadingAttach, setIsUploadingAttach] = useState(false);
  const [handledContactEditRequestId, setHandledContactEditRequestId] =
    useState(0);
  const attachInputRef = useRef<HTMLInputElement>(null);
  const longPressTimerRef = useRef<number | null>(null);
  const longPressFiredRef = useRef(false);
  const [contactEditForm, setContactEditForm] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    jobTitle: '',
    email: '',
    phone: '',
    address: '',
  });

  const displayName =
    formatLeadDisplayName(lead.firstName, lead.lastName) || lead.email || '—';

  const currentTags = useMemo(() => {
    const seen = new Set<string>();
    const tags: string[] = [];
    for (const raw of lead.tags ?? []) {
      const tag = String(raw ?? '').trim();
      if (!tag) continue;
      const key = tag.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      tags.push(tag);
    }
    return tags;
  }, [lead.tags]);

  const openSheet = (next: FieldSheet) => {
    void triggerTapHaptic();
    setTextError(null);
    setContactEditEmailError(null);
    if (next === 'email') setTextDraft(contactForm.email);
    if (next === 'phone') setTextDraft(contactForm.phone);
    if (next === 'tags') {
      setPendingTags(currentTags);
      setTagDraft('');
    }
    if (next === 'event') {
      setPendingEventIds(eventIds);
      setTagDraft('');
    }
    if (next === 'contact-edit') {
      setContactEditForm({
        firstName: firstNameDraft,
        lastName: lastNameDraft,
        companyName: contactForm.companyName,
        jobTitle: contactForm.jobTitle,
        email: contactForm.email,
        phone: contactForm.phone,
        address: contactForm.address,
      });
    }
    setSheet(next);
  };

  const closeSheet = () => setSheet(null);
  /** Cap native: floats above the tab bar; browser: inline (DEV-567). */
  const composerFloats = shellUsesMobileTabBar();

  useEffect(() => {
    if (!composerFloats) return;
    const root = document.documentElement;
    // Lift mobile toasts above the sticky note composer (see ui Toaster).
    root.style.setProperty('--sticky-composer-clearance', '3.75rem');
    return () => {
      root.style.removeProperty('--sticky-composer-clearance');
    };
  }, [composerFloats]);

  if (
    contactEditRequestId > 0 &&
    contactEditRequestId !== handledContactEditRequestId
  ) {
    setHandledContactEditRequestId(contactEditRequestId);
    setContactEditForm({
      firstName: firstNameDraft,
      lastName: lastNameDraft,
      companyName: contactForm.companyName,
      jobTitle: contactForm.jobTitle,
      email: contactForm.email,
      phone: contactForm.phone,
      address: contactForm.address,
    });
    setSheet('contact-edit');
  }

  const handlePhoneRow = () => {
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    if (canManageCrm) {
      openSheet('phone');
    } else if (lead.phone?.trim()) {
      openSheet('phone-actions');
    }
  };

  const clearLongPress = () => {
    if (longPressTimerRef.current != null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const copyContactValue = async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    try {
      await navigator.clipboard.writeText(trimmed);
      void triggerTapHaptic();
      onCopy();
    } catch {
      // Clipboard may be denied; ignore quietly.
    }
  };

  const contactLongPressHandlers = (value: string) => ({
    onPointerDown: () => {
      longPressFiredRef.current = false;
      clearLongPress();
      if (!value.trim()) return;
      longPressTimerRef.current = window.setTimeout(() => {
        longPressFiredRef.current = true;
        void copyContactValue(value);
      }, 480);
    },
    onPointerUp: clearLongPress,
    onPointerLeave: clearLongPress,
    onPointerCancel: clearLongPress,
    onContextMenu: (event: MouseEvent) => {
      event.preventDefault();
      longPressFiredRef.current = true;
      clearLongPress();
      void copyContactValue(value);
    },
  });

  const handleNoteSubmit = () => {
    const trimmed = noteDraft.trim();
    if (!trimmed || isSubmittingComment) return;
    onSubmitComment(trimmed);
    setNoteDraft('');
  };

  const handleAttachFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const workspaceId = (lead.workspaceId ?? '').trim();
    if (!workspaceId) return;
    setIsUploadingAttach(true);
    setActiveTab('attachments');
    try {
      let latest = lead;
      for (const file of Array.from(files)) {
        const upload = await FilesApi.uploadFile({ body: { file } });
        const fileId = upload.data?.file?.id;
        if (!fileId) continue;
        const isImage = file.type.startsWith('image/');
        const attached = await LeadsApi.addAttachment({
          path: { workspaceId, id: latest.id },
          body: {
            fileId,
            kind: isImage
              ? LeadAttachmentKind.IMAGE
              : LeadAttachmentKind.DOCUMENT,
            mimeType: file.type || null,
            title: file.name.replace(/\.[^/.]+$/, '') || file.name,
          },
        });
        latest = attached.data;
      }
      onLeadUpdatedInCache(latest);
    } catch {
      toast.error(attachmentLabels.uploadFailed);
    } finally {
      setIsUploadingAttach(false);
      if (attachInputRef.current) attachInputRef.current.value = '';
    }
  };

  const saveTextField = async (field: 'email' | 'phone') => {
    const previous = contactForm[field];
    const next = textDraft.trim();
    if (next === previous.trim()) {
      closeSheet();
      return;
    }
    if (field === 'email' && next && !isValidLeadEmail(next)) {
      void triggerErrorHaptic();
      setTextError(emailInvalidMessage);
      return;
    }
    setContactForm({ ...contactForm, [field]: next });
    closeSheet();
    const saved = await onContactFieldBlur(field, next);
    if (saved === false && field === 'email' && next) {
      // Server still rejected it: reopen with the typed value.
      setTextDraft(next);
      setTextError(emailInvalidMessage);
      setSheet('email');
    }
  };

  const commitTags = () => {
    const before = new Set(currentTags.map((t) => t.toLowerCase()));
    const after = new Set(pendingTags.map((t) => t.toLowerCase()));
    for (const tag of currentTags) {
      if (!after.has(tag.toLowerCase())) onTagRemove(tag);
    }
    for (const tag of pendingTags) {
      if (!before.has(tag.toLowerCase())) onTagAdd(tag);
    }
    closeSheet();
  };

  const commitEvents = () => {
    const before = new Set(eventIds);
    const after = new Set(pendingEventIds);
    for (const id of eventIds) {
      if (!after.has(id)) onEventsRemove(id);
    }
    for (const id of pendingEventIds) {
      if (!before.has(id)) onEventsAdd(id);
    }
    closeSheet();
  };

  const saveContactEdit = async () => {
    const email = contactEditForm.email.trim();
    if (email && !isValidLeadEmail(email)) {
      void triggerErrorHaptic();
      setContactEditEmailError(emailInvalidMessage);
      return;
    }
    setFirstNameDraft(contactEditForm.firstName);
    setLastNameDraft(contactEditForm.lastName);
    setContactForm({
      email: contactEditForm.email,
      phone: contactEditForm.phone,
      companyName: contactEditForm.companyName,
      jobTitle: contactEditForm.jobTitle,
      address: contactEditForm.address,
    });
    closeSheet();
    onFirstNameBlur();
    onLastNameBlur();
    await onContactFieldBlur('email');
    await onContactFieldBlur('phone');
    await onContactFieldBlur('companyName');
    await onContactFieldBlur('jobTitle');
    await onContactFieldBlur('address');
  };

  const handleSaveToDevice = async () => {
    void triggerTapHaptic();
    if (!leadHasVcfContent(lead)) {
      toast.error(saveToDeviceLabels.toastEmpty);
      return;
    }
    try {
      if (isCapacitorNative()) {
        await saveLeadToDevice(lead);
      } else {
        await shareLeadVcf(lead);
      }
    } catch {
      toast.error(saveToDeviceLabels.toastFailed);
    }
  };

  const statusValue = (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11.5px] font-semibold whitespace-nowrap',
        leadStatusBadgeClass(lead.status),
      )}
    >
      <span
        className="size-1.5 shrink-0 rounded-full"
        style={{ background: leadStatusAccent(lead.status) }}
        aria-hidden
      />
      {statusLabels[lead.status] ?? lead.status}
    </span>
  );

  const assigneeValue = crm.assigneeDisplayReadonly ? (
    <span className="inline-flex min-w-0 items-center gap-1.5 truncate">
      <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-[color:var(--ghost-accent-bg)] text-[8px] font-bold text-[color:var(--ghost-accent-fg)]">
        {personInitials(crm.assigneeDisplayReadonly)}
      </span>
      <span className="truncate">{crm.assigneeDisplayReadonly}</span>
    </span>
  ) : (
    <span className="text-muted-foreground/70">
      {crm.assigneePickerLabels?.nobody ?? '—'}
    </span>
  );

  // Source · who captured it, like the desktop origin card. This used to join
  // the lead's own first name ("Manuell · Anna", DEV-564).
  const herkunftValue = [sourceLabel, crm.creatorValue.trim()]
    .filter(Boolean)
    .join(' · ');
  const consentValue = lead.consentAccepted
    ? formatLeadCapturedAt(lead.createdAt, locale) || detailDict.consentYes
    : detailDict.consentNo;

  const filteredTagOptions = useMemo(() => {
    const q = tagDraft.trim().toLowerCase();
    const pool = [...new Set([...tagSuggestions, ...pendingTags])];
    if (!q) return pool;
    return pool.filter((t) => t.toLowerCase().includes(q));
  }, [tagDraft, tagSuggestions, pendingTags]);

  const canCreateTagFromDraft = (() => {
    const q = tagDraft.trim();
    if (!q) return false;
    return !filteredTagOptions.some((t) => t.toLowerCase() === q.toLowerCase());
  })();

  const filteredEvents = useMemo(() => {
    const q = tagDraft.trim().toLowerCase();
    if (!q) return workspaceEvents;
    return workspaceEvents.filter((e) => e.name.toLowerCase().includes(q));
  }, [tagDraft, workspaceEvents]);

  const noteComposerBar = (
    <div className="pointer-events-auto mx-auto flex max-w-lg items-center">
      <div className="flex min-h-12 w-full min-w-0 items-center gap-2.5 rounded-3xl border border-[color:var(--surface-border)] bg-[color:var(--surface-1,#f4f5f8)]/95 py-1.5 pl-3.5 pr-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.12)] backdrop-blur-[20px]">
        <input
          type="text"
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleNoteSubmit();
            }
          }}
          placeholder={timelineLabels.commentPlaceholder}
          disabled={isSubmittingComment}
          className="min-w-0 flex-1 bg-transparent py-2.5 text-[13px] outline-none placeholder:text-muted-foreground"
          aria-label={timelineLabels.commentPlaceholder}
        />
        <button
          type="button"
          className="shrink-0 text-muted-foreground disabled:opacity-40"
          aria-label={attachmentLabels.add}
          disabled={isUploadingAttach}
          onClick={() => attachInputRef.current?.click()}
        >
          <Paperclip className="size-[17px]" aria-hidden />
        </button>
        <input
          ref={attachInputRef}
          type="file"
          className="hidden"
          multiple
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
          onChange={(e) => void handleAttachFiles(e.target.files)}
        />
        <button
          type="button"
          className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-40"
          disabled={!noteDraft.trim() || isSubmittingComment}
          onClick={handleNoteSubmit}
          aria-label={timelineLabels.commentSubmitAria}
        >
          <ArrowUp className="size-[17px]" aria-hidden />
        </button>
      </div>
    </div>
  );

  return (
    <div
      className={cn(
        'relative flex w-full min-w-0 max-w-full flex-col',
        composerFloats
          ? 'pb-[calc(5.5rem+var(--shell-mobile-tab-bar-clearance,0px))]'
          : 'pb-6',
      )}
    >
      <div className="flex w-full min-w-0 items-center gap-3.5 px-0.5 pt-1">
        <span className="inline-flex size-14 shrink-0 items-center justify-center rounded-[17px] bg-muted text-[20px] font-bold">
          {leadInitials(lead)}
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[20px] font-bold tracking-tight leading-tight">
            {displayName}
          </h1>
          {subtitle ? (
            <p className="mt-0.5 truncate text-[12.5px] text-muted-foreground">
              {subtitle}
            </p>
          ) : null}
        </div>
        {!overflowInHeader && canManageCrm ? (
          <LeadDetailMobileOverflowMenu
            editContactLabel={detailDict.editContact}
            archiveLabel={
              isLeadArchived(lead) ? detailDict.unarchive : detailDict.archive
            }
            deleteLabel={detailDict.delete}
            menuAriaLabel={detailDict.moreActions}
            onEditContact={() => openSheet('contact-edit')}
            onArchive={() => onSetArchived(!isLeadArchived(lead))}
            onDelete={onDeleteRequest}
          />
        ) : null}
      </div>

      {isLeadArchived(lead) ? (
        <Alert variant="warning" className="mt-3.5 py-3">
          <Archive className="size-4" aria-hidden />
          <AlertDescription className="text-[13px] text-foreground">
            {detailDict.archivedBanner}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="mt-3.5 flex w-full min-w-0 gap-2">
        {(
          [
            {
              key: 'call',
              icon: Phone,
              label: detailDict.call,
              disabled: !lead.phone?.trim(),
              onClick: () => void onContactVia(LeadContactChannel.CALL),
            },
            {
              key: 'email',
              icon: Mail,
              label: detailDict.email,
              disabled: !lead.email?.trim(),
              onClick: () => void onContactVia(LeadContactChannel.EMAIL),
            },
            {
              key: 'sms',
              icon: MessageSquare,
              label: detailDict.sms,
              disabled: !lead.phone?.trim(),
              onClick: () => void onContactVia(LeadContactChannel.SMS),
            },
            {
              key: 'save',
              icon: ContactRound,
              label: detailDict.save,
              disabled: false,
              onClick: () => void handleSaveToDevice(),
            },
          ] as const
        ).map((action) => (
          <button
            key={action.key}
            type="button"
            disabled={action.disabled}
            onClick={action.onClick}
            className="flex min-w-0 flex-1 flex-col items-center gap-1.5 rounded-[14px] border border-[color:var(--surface-border)] bg-card px-1 py-2.5 text-[11.5px] font-semibold disabled:opacity-40"
          >
            <action.icon
              className="size-[18px] text-[color:var(--ghost-accent-fg)]"
              aria-hidden
            />
            {action.label}
          </button>
        ))}
      </div>

      <div className="mt-3">
        <DetailListCard>
          <DetailListRow
            icon={CircleDot}
            label={detailDict.status}
            value={statusValue}
            highlighted={sheet === 'status'}
            onClick={() => openSheet('status')}
          />
          <DetailListRow
            icon={UserCheck}
            label={detailDict.assigneeShort}
            value={assigneeValue}
            highlighted={sheet === 'assignee'}
            onClick={() => openSheet('assignee')}
          />
          <DetailListRow
            icon={Users}
            label={detailDict.visibility}
            value={
              crm.visibilityIsWorkspace
                ? detailDict.visibilityWorkspace
                : detailDict.visibilityPrivate
            }
            highlighted={sheet === 'visibility'}
            onClick={() => openSheet('visibility')}
          />
          {eventsEnabled ? (
            <DetailListRow
              icon={Calendar}
              label={detailDict.event}
              value={primaryEventName ?? '—'}
              highlighted={sheet === 'event'}
              onClick={() => openSheet('event')}
            />
          ) : null}
          <DetailListRow
            icon={Tag}
            label={detailDict.tags}
            value={currentTags.length > 0 ? currentTags.join(', ') : '—'}
            highlighted={sheet === 'tags'}
            onClick={() => openSheet('tags')}
          />
        </DetailListCard>
      </div>

      <div className="mt-3">
        <DetailListCard>
          <DetailListRow
            icon={Mail}
            label={detailDict.email}
            value={contactForm.email.trim() || '—'}
            highlighted={sheet === 'email'}
            {...contactLongPressHandlers(contactForm.email)}
            onClick={() => {
              if (longPressFiredRef.current) {
                longPressFiredRef.current = false;
                return;
              }
              if (canManageCrm) {
                openSheet('email');
              } else if (contactForm.email.trim()) {
                void copyContactValue(contactForm.email);
              }
            }}
          />
          <DetailListRow
            icon={Phone}
            label={detailDict.phone}
            value={contactForm.phone.trim() || '—'}
            highlighted={sheet === 'phone' || sheet === 'phone-actions'}
            {...contactLongPressHandlers(contactForm.phone)}
            onClick={handlePhoneRow}
          />
          <DetailListRow
            icon={ScanLine}
            label={detailDict.origin}
            value={herkunftValue || '—'}
            highlighted={sheet === 'herkunft'}
            onClick={() => openSheet('herkunft')}
          />
        </DetailListCard>
      </div>

      <div className="mt-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="border-b border-[color:var(--surface-border)]">
            <TabsList className="h-auto w-full justify-start gap-[18px] rounded-none bg-transparent p-0">
              <TabsTrigger
                value="timeline"
                className="rounded-none border-b-2 border-transparent px-0 pb-2.5 pt-1 text-[13px] data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                {tabsLabels.timeline}
                {(lead.activity?.length ?? 0) > 0 ? (
                  <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">
                    {lead.activity.length}
                  </span>
                ) : null}
              </TabsTrigger>
              <TabsTrigger
                value="attachments"
                className="rounded-none border-b-2 border-transparent px-0 pb-2.5 pt-1 text-[13px] data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                {tabsLabels.attachments}
                <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">
                  {lead.attachments?.length ?? 0}
                </span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="timeline" className="mt-0 space-y-4 pt-3.5">
            {/* Browser: no tab bar, so the note bar sits in the flow above
                the history instead of floating over it (DEV-567). */}
            {composerFloats ? null : noteComposerBar}
            <LeadDetailDescription
              value={lead.description ?? ''}
              placeholder={descriptionPlaceholder}
              messageFromLead={messageFromLead}
              messageLabel={messageLabel}
              isSaving={isSavingDescription}
              onSave={onDescriptionSave}
            />
            <LeadDetailTimeline
              key={lead.id}
              lead={lead}
              locale={locale}
              labels={timelineLabels}
              statusLabels={statusLabels}
              resolveAuthorDisplayName={resolveAuthorDisplayName}
              resolveAuthorInitials={resolveAuthorInitials}
              isSubmittingComment={isSubmittingComment}
              onSubmitComment={onSubmitComment}
              currentUserId={currentUserId}
              noteActionLabels={detailDict.noteActions}
              onUpdateNote={onUpdateNote}
              onDeleteNote={onDeleteNote}
              hideTitle
              hideComposer
            />
          </TabsContent>

          <TabsContent value="attachments" className="mt-0 pt-3.5">
            <LeadDetailAttachments
              lead={lead}
              labels={attachmentLabels}
              onUpdated={onLeadUpdatedInCache}
            />
          </TabsContent>
        </Tabs>
      </div>

      {composerFloats ? (
        <div
          className={cn(
            'pointer-events-none fixed inset-x-0 z-40 px-4',
            'bottom-[max(0.75rem,calc(var(--shell-mobile-tab-bar-clearance,0px)+0.5rem))]',
          )}
        >
          {noteComposerBar}
        </div>
      ) : null}

      {/* Status sheet — immediate select */}
      <Drawer
        open={sheet === 'status'}
        onOpenChange={(open) => !open && closeSheet()}
      >
        <DrawerContent fitContent scrollable={false} className="rounded-t-3xl">
          <SheetChrome
            title={detailDict.status}
            onCancel={closeSheet}
            showDone={false}
          >
            <div className="flex flex-col gap-0.5 px-2 pb-6">
              {LEAD_STATUS_IDS.map((status) => {
                const selected = lead.status === status;
                return (
                  <button
                    key={status}
                    type="button"
                    className={cn(
                      'flex min-h-[52px] items-center gap-3 rounded-xl px-3 text-[15px]',
                      selected && 'bg-[color:var(--surface-2)] font-semibold',
                    )}
                    onClick={() => {
                      void triggerSelectionHaptic();
                      closeSheet();
                      if (status === lead.status) return;
                      void onStatusChange(status);
                    }}
                  >
                    <span
                      className="size-[7px] shrink-0 rounded-full"
                      style={{ background: leadStatusAccent(status) }}
                      aria-hidden
                    />
                    <span className="flex-1 text-left">
                      {statusLabels[status] ?? status}
                    </span>
                    {selected ? (
                      <Check className="size-[18px] text-primary" aria-hidden />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </SheetChrome>
        </DrawerContent>
      </Drawer>

      {/* Assignee sheet */}
      <Drawer
        open={sheet === 'assignee'}
        onOpenChange={(open) => !open && closeSheet()}
      >
        <DrawerContent fitContent className="rounded-t-3xl">
          <SheetChrome
            title={detailDict.assigneeShort}
            cancelLabel={detailDict.cancel}
            onCancel={closeSheet}
            showDone={false}
          >
            <div className="px-1 pb-2">
              {crm.workspaceId ? (
                <LeadAssigneePicker
                  workspaceId={crm.workspaceId}
                  ownerUserId={crm.leadOwnerUserId}
                  ownerOptionLabel={crm.recordOwnerAssigneeLabel}
                  assigneeUserId={crm.assigneeUserId ?? undefined}
                  triggerLabel={crm.assigneeTriggerLabel}
                  editable={canManageCrm}
                  placeholder={crm.assigneePlaceholder}
                  labels={crm.assigneePickerLabels}
                  locale={crm.locale}
                  isPrivate={crm.isPrivate}
                  workspaceLeadsAlwaysShared={crm.leadsSharingLockedByWorkspace}
                  presentation="inline"
                  confirmLayout="sheet"
                  onAssigneeChange={(next, label) => {
                    crm.onAssigneeChange?.(next, label);
                    closeSheet();
                  }}
                />
              ) : (
                <p className="px-3 py-4 text-[13px] text-muted-foreground">
                  {crm.shareReadOnlyHint}
                </p>
              )}
            </div>
          </SheetChrome>
        </DrawerContent>
      </Drawer>

      {/* Visibility sheet */}
      <Drawer
        open={sheet === 'visibility'}
        onOpenChange={(open) => !open && closeSheet()}
      >
        <DrawerContent fitContent scrollable={false} className="rounded-t-3xl">
          <SheetChrome
            title={detailDict.visibility}
            onCancel={closeSheet}
            showDone={false}
          >
            <div className="flex flex-col gap-0.5 px-2 pb-6">
              {(
                [
                  {
                    value: 'private' as const,
                    label: detailDict.visibilityPrivate,
                    icon: Lock,
                  },
                  {
                    value: 'workspace' as const,
                    label: detailDict.visibilityWorkspace,
                    icon: Users,
                  },
                ] as const
              ).map((opt) => {
                const selected =
                  (opt.value === 'workspace') === crm.visibilityIsWorkspace;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={
                      !canManageCrm || crm.leadsSharingLockedByWorkspace
                    }
                    className={cn(
                      'flex min-h-[52px] items-center gap-3 rounded-xl px-3 text-[15px]',
                      selected && 'bg-[color:var(--surface-2)] font-semibold',
                    )}
                    onClick={() => {
                      if (!canManageCrm || crm.leadsSharingLockedByWorkspace) {
                        return;
                      }
                      const previous = crm.visibilityIsWorkspace;
                      const next = opt.value === 'workspace';
                      if (next === previous) {
                        closeSheet();
                        return;
                      }
                      crm.onShareChange?.(next);
                      closeSheet();
                    }}
                  >
                    <opt.icon
                      className="size-4 shrink-0 text-muted-foreground"
                      aria-hidden
                    />
                    <span className="flex-1 text-left">{opt.label}</span>
                    {selected ? (
                      <Check className="size-[18px] text-primary" aria-hidden />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </SheetChrome>
        </DrawerContent>
      </Drawer>

      {/* Tags multi sheet */}
      <Drawer
        open={sheet === 'tags'}
        onOpenChange={(open) => !open && closeSheet()}
      >
        <DrawerContent fitContent className="rounded-t-3xl">
          <SheetChrome
            title={detailDict.tags}
            cancelLabel={detailDict.cancel}
            doneLabel={detailDict.done}
            onCancel={closeSheet}
            onDone={canEditTags ? commitTags : closeSheet}
          >
            <div className="px-4 pb-2">
              <div className="flex min-h-11 items-center gap-2 rounded-xl bg-[color:var(--surface-2)] px-3 text-[15px]">
                <Search
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  placeholder={detailDict.searchTags}
                  className="h-11 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  disabled={!canEditTags}
                />
              </div>
            </div>
            <div className="max-h-[50vh] overflow-y-auto px-2 pb-6">
              {canCreateTagFromDraft && canEditTags ? (
                <button
                  type="button"
                  className="flex min-h-12 w-full items-center gap-3 px-3 text-[15px]"
                  onClick={() => {
                    const next = tagDraft.trim();
                    setPendingTags((prev) => [...prev, next]);
                    setTagDraft('');
                  }}
                >
                  <span className="flex size-[22px] items-center justify-center rounded-[7px] bg-primary text-primary-foreground">
                    <Check className="size-3.5" aria-hidden />
                  </span>
                  <span className="flex-1 text-left">{tagDraft.trim()}</span>
                  <span className="text-[11.5px] font-semibold text-[color:var(--ghost-accent-fg)]">
                    {detailDict.createTag}
                  </span>
                </button>
              ) : null}
              {filteredTagOptions.map((tag) => {
                const checked = pendingTags.some(
                  (t) => t.toLowerCase() === tag.toLowerCase(),
                );
                return (
                  <button
                    key={tag}
                    type="button"
                    disabled={!canEditTags}
                    className="flex min-h-12 w-full items-center gap-3 px-3 text-[15px]"
                    onClick={() => {
                      setPendingTags((prev) =>
                        checked
                          ? prev.filter(
                              (t) => t.toLowerCase() !== tag.toLowerCase(),
                            )
                          : [...prev, tag],
                      );
                    }}
                  >
                    <span
                      className={cn(
                        'flex size-[22px] items-center justify-center rounded-[7px] border-[1.5px]',
                        checked
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-[color:var(--surface-border)]',
                      )}
                    >
                      {checked ? (
                        <Check className="size-3.5" aria-hidden />
                      ) : null}
                    </span>
                    <span className="flex-1 text-left">{tag}</span>
                  </button>
                );
              })}
            </div>
          </SheetChrome>
        </DrawerContent>
      </Drawer>

      {/* Events multi sheet */}
      <Drawer
        open={sheet === 'event'}
        onOpenChange={(open) => !open && closeSheet()}
      >
        <DrawerContent fitContent className="rounded-t-3xl">
          <SheetChrome
            title={detailDict.event}
            cancelLabel={detailDict.cancel}
            doneLabel={detailDict.done}
            onCancel={closeSheet}
            onDone={canEditEvents ? commitEvents : closeSheet}
          >
            <div className="px-4 pb-2">
              <div className="flex min-h-11 items-center gap-2 rounded-xl bg-[color:var(--surface-2)] px-3 text-[15px]">
                <Search
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  placeholder={detailDict.event}
                  className="h-11 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  disabled={!canEditEvents}
                />
              </div>
            </div>
            <div className="max-h-[50vh] overflow-y-auto px-2 pb-6">
              {filteredEvents.map((ev) => {
                const checked = pendingEventIds.includes(ev.id);
                return (
                  <button
                    key={ev.id}
                    type="button"
                    disabled={!canEditEvents}
                    className="flex min-h-12 w-full items-center gap-3 px-3 text-[15px]"
                    onClick={() => {
                      setPendingEventIds((prev) =>
                        checked
                          ? prev.filter((id) => id !== ev.id)
                          : [...prev, ev.id],
                      );
                    }}
                  >
                    <span
                      className={cn(
                        'flex size-[22px] items-center justify-center rounded-[7px] border-[1.5px]',
                        checked
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-[color:var(--surface-border)]',
                      )}
                    >
                      {checked ? (
                        <Check className="size-3.5" aria-hidden />
                      ) : null}
                    </span>
                    <span className="flex-1 text-left">{ev.name}</span>
                  </button>
                );
              })}
            </div>
          </SheetChrome>
        </DrawerContent>
      </Drawer>

      {/* Text field sheets */}
      {(['email', 'phone'] as const).map((field) => (
        <Drawer
          key={field}
          open={sheet === field}
          onOpenChange={(open) => !open && closeSheet()}
        >
          <DrawerContent fitContent className="rounded-t-3xl">
            <SheetChrome
              title={field === 'email' ? detailDict.email : detailDict.phone}
              cancelLabel={detailDict.cancel}
              doneLabel={detailDict.done}
              onCancel={closeSheet}
              onDone={() => void saveTextField(field)}
            >
              <div className="flex flex-col gap-2 px-4 pb-6">
                <div className="relative flex min-h-[50px] items-center rounded-[14px] border border-ring px-3.5 shadow-[0_0_0_3px_color-mix(in_oklab,var(--ring)_16%,transparent)]">
                  <Input
                    autoFocus
                    type={field === 'email' ? 'email' : 'tel'}
                    inputMode={field === 'phone' ? 'tel' : 'email'}
                    value={textDraft}
                    onChange={(e) => {
                      setTextDraft(e.target.value);
                      setTextError(null);
                    }}
                    aria-invalid={field === 'email' && Boolean(textError)}
                    aria-describedby={
                      field === 'email' && textError
                        ? 'lead-mobile-email-error'
                        : undefined
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void saveTextField(field);
                      }
                    }}
                    className="h-12 border-0 bg-transparent px-0 text-[16px] shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                  {textDraft ? (
                    <button
                      type="button"
                      className="absolute right-3 text-muted-foreground"
                      onClick={() => setTextDraft('')}
                      aria-label={detailDict.cancel}
                    >
                      <X className="size-[15px]" aria-hidden />
                    </button>
                  ) : null}
                </div>
                {field === 'phone' ? (
                  <p className="text-xs text-muted-foreground">
                    {detailDict.phoneFieldHint}
                  </p>
                ) : null}
                {field === 'email' && textError ? (
                  <p
                    id="lead-mobile-email-error"
                    className="text-xs text-destructive"
                    role="alert"
                  >
                    {textError}
                  </p>
                ) : null}
              </div>
            </SheetChrome>
          </DrawerContent>
        </Drawer>
      ))}

      {/* Read-only phone actions */}
      <Drawer
        open={sheet === 'phone-actions'}
        onOpenChange={(open) => !open && closeSheet()}
      >
        <DrawerContent fitContent scrollable={false} className="rounded-t-3xl">
          <DrawerHeader className="px-4 pb-1 pt-1">
            <DrawerTitle className="text-center text-[13px] font-normal text-muted-foreground">
              {contactForm.phone}
            </DrawerTitle>
          </DrawerHeader>
          <div className="flex flex-col gap-2 px-4 pb-2">
            <Button
              type="button"
              variant="secondary"
              className="h-[50px] rounded-[14px] text-[15px] font-semibold text-[color:var(--ghost-accent-fg)]"
              onClick={() => {
                closeSheet();
                void onContactVia(LeadContactChannel.CALL);
              }}
            >
              {detailDict.call}
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="h-[50px] rounded-[14px] text-[15px] font-semibold"
              onClick={() => {
                closeSheet();
                void onContactVia(LeadContactChannel.SMS);
              }}
            >
              {detailDict.sendSms}
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="h-[50px] rounded-[14px] text-[15px] font-semibold"
              onClick={() => {
                closeSheet();
                onCopy();
              }}
            >
              {detailDict.copyAction}
            </Button>
          </div>
          <p className="flex items-center justify-center gap-1.5 px-4 pb-7 text-xs text-muted-foreground">
            <Lock className="size-[13px]" aria-hidden />
            {detailDict.readOnlyEditHint}
          </p>
        </DrawerContent>
      </Drawer>

      {/* Herkunft read-only */}
      <Drawer
        open={sheet === 'herkunft'}
        onOpenChange={(open) => !open && closeSheet()}
      >
        <DrawerContent fitContent scrollable={false} className="rounded-t-3xl">
          <SheetChrome
            title={detailDict.origin}
            cancelLabel={detailDict.cancel}
            onCancel={closeSheet}
            showDone={false}
          >
            <div className="px-4 pb-8 text-[14px] text-muted-foreground">
              <p>
                <span className="font-medium text-foreground">
                  {detailDict.source}:
                </span>{' '}
                {sourceLabel}
              </p>
              <p className="mt-2">
                <span className="font-medium text-foreground">
                  {detailDict.capturedBy}:
                </span>{' '}
                {crm.creatorValue.trim() || '—'}
              </p>
              {primaryEventName ? (
                <p className="mt-2">
                  <span className="font-medium text-foreground">
                    {detailDict.event}:
                  </span>{' '}
                  {primaryEventName}
                </p>
              ) : null}
              <p className="mt-2">
                <span className="font-medium text-foreground">
                  {detailDict.consent}:
                </span>{' '}
                {consentValue}
              </p>
            </div>
          </SheetChrome>
        </DrawerContent>
      </Drawer>

      {/* Full contact edit */}
      <Drawer
        open={sheet === 'contact-edit'}
        onOpenChange={(open) => !open && closeSheet()}
      >
        <DrawerContent
          fitContent={false}
          className="h-[min(92vh,calc(100dvh-1rem))] rounded-t-3xl"
        >
          <SheetChrome
            title={detailDict.editContactTitle}
            cancelLabel={detailDict.cancel}
            doneLabel={detailDict.secureSave}
            onCancel={closeSheet}
            onDone={() => void saveContactEdit()}
          >
            <div className="flex flex-col gap-3 px-4 pb-8">
              {(
                [
                  ['firstName', createDict.firstName],
                  ['lastName', createDict.lastName],
                  ['companyName', createDict.workspace],
                  ['jobTitle', createDict.jobTitle],
                  ['email', createDict.email],
                  ['phone', createDict.phone],
                  ['address', createDict.address],
                ] as const
              ).map(([key, label], index) => (
                <label key={key} className="flex flex-col gap-1.5">
                  <span className="text-xs text-muted-foreground">{label}</span>
                  <Input
                    value={contactEditForm[key]}
                    maxLength={
                      key === 'firstName' || key === 'lastName'
                        ? LEAD_NAME_MAX_LENGTH
                        : undefined
                    }
                    placeholder={
                      key === 'address' ? detailDict.addAddress : undefined
                    }
                    onChange={(e) => {
                      setContactEditForm((prev) => ({
                        ...prev,
                        [key]: e.target.value,
                      }));
                      if (key === 'email') setContactEditEmailError(null);
                    }}
                    aria-invalid={
                      key === 'email' && Boolean(contactEditEmailError)
                    }
                    className={cn(
                      'h-[46px] rounded-xl bg-[color:var(--surface-2)] text-[15px]',
                      index === 0 &&
                        'border-ring bg-card shadow-[0_0_0_3px_color-mix(in_oklab,var(--ring)_16%,transparent)]',
                    )}
                    type={
                      key === 'email'
                        ? 'email'
                        : key === 'phone'
                          ? 'tel'
                          : 'text'
                    }
                  />
                  {key === 'email' && contactEditEmailError ? (
                    <span className="text-xs text-destructive" role="alert">
                      {contactEditEmailError}
                    </span>
                  ) : null}
                </label>
              ))}
            </div>
          </SheetChrome>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
